import React, { useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { Alert, Box, Grid, TextField, Typography } from '@mui/material';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import MoneyOffOutlinedIcon from '@mui/icons-material/MoneyOffOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { api } from 'common';
import { formatMoney } from 'common/src/other/currencyFormat';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2DocsChip,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
} from '../components/Table/C2UserTable';

const STATUS_UI = {
  pending: { tone: 'warning', label: 'Pendiente' },
  refunded: { tone: 'success', label: 'Comisión reintegrada' },
  resolved: { tone: 'violet', label: 'Atendido' },
  dismissed: { tone: 'neutral', label: 'Descartado' },
};
const REASON_TONE = { no_payment: 'danger', bad_manners: 'warning', not_welcome: 'neutral' };

const NOTE_MAX = 500;
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
};

function InfoRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: `1px solid ${C2.cellBorder}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

/** `reports`: lista de user_reports (null mientras carga); la suscripción vive en la vista de Quejas. */
export default function UserReports({ reports, loadError }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((s) => s.settingsdata.settings);
  const showContact = !!settings?.AllowCriticalEditsAdmin;

  const [statusFilter, setStatusFilter] = useState('pending');
  const [reasonFilter, setReasonFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const money = (v) => formatMoney(Number(v) || 0, settings || {});
  const rows = useMemo(() => reports || [], [reports]);

  // Cuántas veces han reportado a cada pasajero y cuántos reportes ha hecho cada conductor.
  const counts = useMemo(() => {
    const byCustomer = {};
    const unpaidByCustomer = {};
    const byDriver = {};
    rows.forEach((r) => {
      byCustomer[r.customerUid] = (byCustomer[r.customerUid] || 0) + 1;
      if (r.status === 'refunded') unpaidByCustomer[r.customerUid] = (unpaidByCustomer[r.customerUid] || 0) + 1;
      byDriver[r.driverUid] = (byDriver[r.driverUid] || 0) + 1;
    });
    return { byCustomer, unpaidByCustomer, byDriver };
  }, [rows]);

  const summary = useMemo(() => {
    const monthStart = moment().startOf('month').valueOf();
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      pendingUnpaid: rows.filter((r) => r.status === 'pending' && r.reason === 'no_payment').length,
      refundedMonth: rows
        .filter((r) => r.status === 'refunded' && (Number(r.refundedAt) || 0) >= monthStart)
        .reduce((sum, r) => sum + (Number(r.refundedAmount) || 0), 0),
      reportedCustomers: Object.keys(counts.byCustomer).length,
    };
  }, [rows, counts]);

  const filtered = rows.filter((r) => (!statusFilter || r.status === statusFilter) && (!reasonFilter || r.reason === reasonFilter));
  const current = openId ? rows.find((r) => r.id === openId) : null;

  // Errores de la acción: se muestran dentro de la ventana (el aviso de la página queda tapado por el modal).
  const [dialogNotice, setDialogNotice] = useState(null);

  const openReport = (row) => {
    setOpenId(row.id);
    setNote(row.adminNote || '');
    setDialogNotice(null);
  };

  const runAction = async (action) => {
    if (!current) return;
    setSaving(true);
    setDialogNotice(null);
    const result = await api.adminUserReportAction({ reportId: current.id, action, note });
    setSaving(false);
    if (!result.success) {
      setDialogNotice(result.error);
      return;
    }
    const msgs = {
      refund: `Se reintegraron ${money(result.amount)} a la billetera de ${current.driverName || 'el conductor'}. Al pasajero se le pedirá pago por adelantado en sus próximos viajes.`,
      dismiss: 'Reporte descartado. No se reintegró la comisión.',
      resolve: 'Reporte marcado como atendido.',
    };
    setNotice({ severity: 'success', msg: msgs[action] });
    setOpenId(null);
  };

  const columns = [
    {
      title: 'Fecha',
      field: 'createdAt',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.85rem', color: C2.sidebar }}>{moment(r.createdAt).format('lll')}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{moment(r.createdAt).fromNow()}</Typography>
        </Box>
      ),
    },
    {
      title: 'Motivo',
      field: 'reason',
      render: (r) => <C2StatusChip tone={REASON_TONE[r.reason] || 'neutral'} label={r.reasonLabel || api.userReportReasonLabel(r.reason)} />,
    },
    {
      title: 'Pasajero reportado',
      field: 'customerName',
      customFilterAndSearch: (term, r) => `${r.customerName || ''} ${r.customerMobile || ''}`.toLowerCase().includes(String(term).toLowerCase()),
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 150 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.customerName || 'Sin nombre'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: (counts.byCustomer[r.customerUid] || 0) > 1 ? '#B45309' : C2.muted, fontWeight: (counts.byCustomer[r.customerUid] || 0) > 1 ? 700 : 400 }}>
            {`${counts.byCustomer[r.customerUid] || 0} reporte(s)`}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Reportó',
      field: 'driverName',
      customFilterAndSearch: (term, r) => `${r.driverName || ''} ${r.driverMobile || ''}`.toLowerCase().includes(String(term).toLowerCase()),
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 140 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.driverName || 'Conductor'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>{`${counts.byDriver[r.driverUid] || 0} reporte(s) hechos`}</Typography>
        </Box>
      ),
    },
    {
      title: 'Reserva',
      field: 'reference',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography component={Link} to={`/bookings/bookingdetails/${r.bookingId}`} onClick={(e) => e.stopPropagation()} sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.85rem', color: C2.violet }}>
            {r.reference || r.bookingId}
          </Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{`${money(r.tripCost)} · ${t(r.paymentMode) || r.paymentMode}`}</Typography>
        </Box>
      ),
    },
    {
      title: 'Comisión',
      field: 'commission',
      render: (r) => (r.reason === 'no_payment' ? money(r.status === 'refunded' ? r.refundedAmount : r.commission) : '—'),
    },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => <C2StatusChip tone={(STATUS_UI[r.status] || STATUS_UI.pending).tone} label={(STATUS_UI[r.status] || STATUS_UI.pending).label} />,
    },
  ];

  const toolbar = c2Toolbar(
    <C2Select
      key="status"
      id="report-status"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={170}
      options={[{ value: '', label: 'Todos' }, ...Object.keys(STATUS_UI).map((k) => ({ value: k, label: STATUS_UI[k].label }))]}
    />,
    <C2Select
      key="reason"
      id="report-reason"
      label="Motivo"
      value={reasonFilter}
      onChange={(e) => setReasonFilter(e.target.value)}
      minWidth={170}
      options={[{ value: '', label: 'Todos' }, ...api.USER_REPORT_REASONS]}
    />
  );

  if (loadError) return <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: C2_FONT }}>{loadError}</Alert>;
  if (!reports) return <CircularLoading />;

  const pending = current && current.status === 'pending';

  return (
    <>
      {notice ? (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
          {notice.msg}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={ReportGmailerrorredOutlinedIcon} label="Pendientes" value={summary.pending} caption="Reportes sin revisar" accent={summary.pending ? '#D97706' : C2.violet} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={MoneyOffOutlinedIcon} label="No pago por revisar" value={summary.pendingUnpaid} caption="Pueden requerir reintegro" accent={summary.pendingUnpaid ? '#DC2626' : C2.violet} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={PaidOutlinedIcon} label="Reintegrado este mes" value={money(summary.refundedMonth)} caption="Comisiones devueltas a conductores" accent="#16A34A" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={GroupOutlinedIcon} label="Pasajeros reportados" value={summary.reportedCustomers} caption={`${rows.length} reportes en total`} />
        </Grid>
      </Grid>

      <C2TableCard>
        <C2SectionHeading
          title="Reporte de usuarios"
          subtitle="Llegan desde Ajustes › Calificar/Reportar usuario en la app del conductor. En 'No pagó el viaje' revisa la reserva y, si no hubo pago, reintegra la comisión."
        />
        <MaterialTable
          title="Reporte de usuarios"
          columns={columns}
          data={filtered}
          style={c2TableStyle(isRTL)}
          components={toolbar}
          onRowClick={(evt, row) => {
            setSelectedRow(row.tableData.id);
            openReport(row);
          }}
          options={{
            ...c2TableOptions(selectedRow),
            actionsColumnIndex: -1,
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            rowStyle: (row) => ({
              backgroundColor: row.status === 'pending' ? '#FFFBEB' : selectedRow === row.tableData.id ? C2.lavender : '#FFFFFF',
              fontFamily: C2_FONT,
            }),
          }}
          localization={c2Localization(
            localization(t),
            <C2EmptyState
              icon={ReportGmailerrorredOutlinedIcon}
              title={statusFilter || reasonFilter ? 'No hay reportes con estos filtros' : 'Aún no hay reportes'}
              message="Cuando un conductor reporte a un pasajero desde la app, aparecerá aquí."
            />
          )}
          actions={[
            (row) => ({
              icon: () => <C2DocsChip icon={ReportGmailerrorredOutlinedIcon} label={row.status === 'pending' ? 'Revisar' : 'Ver'} />,
              tooltip: row.status === 'pending' ? 'Revisar reporte' : 'Ver detalle',
              onClick: (e, r) => openReport(r),
            }),
          ]}
        />
      </C2TableCard>

      <C2Dialog
        open={!!current}
        onClose={() => (saving ? null : setOpenId(null))}
        title={current ? `${current.reasonLabel || api.userReportReasonLabel(current.reason)} · ${current.customerName || 'Pasajero'}` : 'Reporte'}
        icon={ReportGmailerrorredOutlinedIcon}
        maxWidth={720}
        actions={
          <>
            <C2SecondaryButton onClick={() => setOpenId(null)} disabled={saving}>
              Cerrar
            </C2SecondaryButton>
            {pending ? (
              <C2SecondaryButton onClick={() => runAction('dismiss')} disabled={saving}>
                Descartar
              </C2SecondaryButton>
            ) : null}
            {pending && current.reason === 'no_payment' ? (
              <C2PrimaryButton onClick={() => runAction('refund')} disabled={saving || !(Number(current.commission) > 0)}>
                {saving ? 'Procesando…' : `Reintegrar comisión (${money(current.commission)})`}
              </C2PrimaryButton>
            ) : null}
            {pending && current.reason !== 'no_payment' ? (
              <C2PrimaryButton onClick={() => runAction('resolve')} disabled={saving}>
                {saving ? 'Guardando…' : 'Marcar atendido'}
              </C2PrimaryButton>
            ) : null}
          </>
        }
      >
        {current ? (
          <Grid container spacing={2}>
            {!pending ? (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: '12px', fontFamily: C2_FONT }}>
                  {`Este reporte ya fue atendido: ${(STATUS_UI[current.status] || STATUS_UI.pending).label.toLowerCase()}`}
                  {current.resolvedByName ? ` por ${current.resolvedByName}` : ''}
                  {current.resolvedAt ? ` el ${moment(current.resolvedAt).format('lll')}` : ''}
                  {current.status === 'refunded' && current.refundedAmount ? ` (${money(current.refundedAmount)}).` : '.'}
                </Alert>
              </Grid>
            ) : null}
            {dialogNotice ? (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setDialogNotice(null)} sx={{ borderRadius: '12px', fontFamily: C2_FONT }}>
                  {dialogNotice}
                </Alert>
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <InfoRow label="Motivo">
                <C2StatusChip tone={REASON_TONE[current.reason] || 'neutral'} label={current.reasonLabel || api.userReportReasonLabel(current.reason)} />
              </InfoRow>
              <InfoRow label="Reserva">
                <Link to={`/bookings/bookingdetails/${current.bookingId}`} style={{ color: C2.violet }}>
                  {current.reference || current.bookingId}
                </Link>
              </InfoRow>
              <InfoRow label="Fecha del viaje">{current.tripDate ? moment(current.tripDate).format('lll') : '—'}</InfoRow>
              <InfoRow label="Valor del viaje">{`${money(current.tripCost)} · ${t(current.paymentMode) || current.paymentMode}`}</InfoRow>
              {current.reason === 'no_payment' ? (
                <InfoRow label="Comisión descontada">{money(current.commission)}</InfoRow>
              ) : null}
              <InfoRow label="Reportado">{`${moment(current.createdAt).format('lll')} (${moment(current.createdAt).fromNow()})`}</InfoRow>
              <InfoRow label="Estado">
                <C2StatusChip tone={(STATUS_UI[current.status] || STATUS_UI.pending).tone} label={(STATUS_UI[current.status] || STATUS_UI.pending).label} />
              </InfoRow>
              {current.resolvedAt ? (
                <InfoRow label="Atendió">{`${current.resolvedByName || ''} · ${moment(current.resolvedAt).format('lll')}`}</InfoRow>
              ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow label="Pasajero">{current.customerName || '—'}</InfoRow>
              <InfoRow label="Celular pasajero">{showContact ? current.customerMobile || '—' : t('hidden_demo')}</InfoRow>
              <InfoRow label="Reportes de este pasajero">
                {`${counts.byCustomer[current.customerUid] || 0} (${counts.unpaidByCustomer[current.customerUid] || 0} no pago confirmado)`}
              </InfoRow>
              <InfoRow label="Conductor">{current.driverName || '—'}</InfoRow>
              <InfoRow label="Celular conductor">{showContact ? current.driverMobile || '—' : t('hidden_demo')}</InfoRow>
              <InfoRow label="Reportes de este conductor">{counts.byDriver[current.driverUid] || 0}</InfoRow>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.8rem', color: C2.muted, mb: 0.75 }}>Comentario del conductor</Typography>
              <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: C2.lavender, border: `1px solid ${C2.border}`, fontFamily: C2_FONT, fontSize: '0.9rem', color: C2.sidebar, whiteSpace: 'pre-wrap' }}>
                {current.comment || 'Sin comentario'}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                disabled={!pending}
                placeholder="Nota interna: qué revisaste (llamada al pasajero, chat, recorrido)…"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                helperText={pending ? `${note.length}/${NOTE_MAX}` : ''}
                sx={fieldSx}
              />
              {pending && current.reason === 'no_payment' ? (
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, mt: 1 }}>
                  Al reintegrar, el conductor recibe la comisión en su billetera con una notificación, y al pasajero se le mostrará "Solicitar pago por adelantado" en sus próximas solicitudes. Solo el administrador principal puede reintegrar.
                </Typography>
              ) : null}
            </Grid>
          </Grid>
        ) : null}
      </C2Dialog>
    </>
  );
}
